/* ==========================================================================
   HUSSIENI HOME — CONFIGURATOR 3D VIEW

   Nothing here runs until the customer asks for the 3D view. Only then is the
   viewer library fetched, and only then the model.

   Faults this file exists to avoid, all found by testing on the real preview
   rather than locally:

   1. loading="eager" is mandatory. Without it model-viewer defers the fetch
      to its own preload heuristic and logs "$updateSource BAILING OUT EARLY",
      leaving the panel spinning forever even though the element is on screen.
      This was the actual cause of the 3D never appearing.

   2. The viewer must never be created inside a hidden container. An element
      with no layout box gets a zero-sized canvas. The host keeps its
      dimensions at all times and the loading and error panels sit over it.

   3. A stray error event must not destroy a working view. model-viewer emits
      an error event for non-fatal things, and a lost WebGL context is
      routinely recovered. After a successful load, later errors are quiet.

   4. A load can stall with neither load nor error. A bounded timeout turns
      that into a retryable state rather than an endless spinner.

   There can be more than one control opening the view, so every control is
   bound, not the first.

   The 360 button drives model-viewer's own turntable. Dragging already turned
   the piece a full revolution and more, but a rotation you only find by
   guessing that the image is draggable is not a 360 view as far as anyone
   looking at the page is concerned. The turntable stops when the view is
   closed, so a hidden canvas never keeps a GPU busy.

   Switching back to photography hides the viewer but keeps it alive. Tearing
   it down meant every switch paid for a fresh parse and a fresh WebGL context,
   which read as lag on exactly the action a customer repeats most.

   Honesty rules enforced in code:
   - Colour comes only from an approved sample. An option carrying neither a
     colour nor a surface value is reported as not represented, never guessed.
   - Each group writes only to the material its own data names.
   - The camera is never reset when materials change.
   ========================================================================== */
(function () {
  'use strict';

  var VIEWER_SRC =
    'https://cdn.jsdelivr.net/npm/@google/model-viewer@4.3.1/dist/model-viewer.min.js';
  var LOAD_TIMEOUT = 30000;
  // The opening view. Every model in this catalogue is built with its front on
  // +Z, which is where model-viewer measures 0deg from, so a small positive
  // azimuth is a three-quarter view of the product's front.
  //
  // This was 325deg, a three-quarter from the front LEFT. On a piece that is
  // symmetric it makes no difference, but ORBIT has a tall upholstered arm
  // panel at its left end, and from the left that panel stands in front of
  // everything else: the timber disc, the boucle pad and most of the seat were
  // hidden behind it, so the view a customer opened did not read as the chair
  // they were looking at. From +20deg the whole composition is visible, and on
  // ELIO and AURA the two angles are indistinguishable.
  var ORBIT = '20deg 80deg auto';
  var FOV = 28;
  var FOV_MIN = 16;
  var FOV_MAX = 45;
  var SPIN_SPEED = '18deg';
  var libPromise = null;

  function loadLibrary() {
    if (libPromise) return libPromise;
    libPromise = new Promise(function (resolve, reject) {
      if (window.customElements && customElements.get('model-viewer')) return resolve();
      var s = document.createElement('script');
      s.type = 'module';
      s.src = VIEWER_SRC;
      s.onerror = function () { libPromise = null; reject(new Error('library')); };
      document.head.appendChild(s);
      var waited = 0;
      var t = setInterval(function () {
        if (customElements.get('model-viewer')) { clearInterval(t); resolve(); }
        else if ((waited += 100) > 15000) { clearInterval(t); libPromise = null; reject(new Error('timeout')); }
      }, 100);
    });
    return libPromise;
  }

  function hexToLinear(hex) {
    var h = String(hex).replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var f = [0, 2, 4].map(function (i) {
      var c = parseInt(h.substr(i, 2), 16) / 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return [f[0], f[1], f[2], 1];
  }

  function has(v) { return v !== null && v !== undefined && v !== ''; }

  function init(root) {
    if (root.dataset.hhV3dReady === '1') return;
    root.dataset.hhV3dReady = '1';

    var cfg;
    try { cfg = JSON.parse(root.querySelector('[data-hh-v3d-data]').textContent); }
    catch (e) { return; }

    var openBtns  = root.querySelectorAll('[data-hh-v3d-open]');
    var closeBtns = root.querySelectorAll('[data-hh-v3d-close]');
    var stage    = root.querySelector('[data-hh-v3d-stage]');
    var host     = root.querySelector('[data-hh-v3d-host]');
    var loading  = root.querySelector('[data-hh-v3d-loading]');
    var errBox   = root.querySelector('[data-hh-v3d-error]');
    var retry    = root.querySelector('[data-hh-v3d-retry]');
    var controls = root.querySelector('[data-hh-v3d-controls]');
    var spinBtn  = root.querySelector('[data-hh-v3d-act="spin"]');
    var hint     = root.querySelector('[data-hh-v3d-hint]');
    var note     = root.querySelector('[data-hh-v3d-note]');
    var visual   = document.querySelector('[data-hh-pdp-visual]');
    var cfgRoot  = document.querySelector('[data-hh-cfg]');

    // A product may name its own opening view. Nothing does yet; the default
    // above suits every model in the catalogue. It is read here so that a
    // piece that needs a different angle is data, not another code change.
    var startOrbit = (cfg.view && String(cfg.view).trim()) ? String(cfg.view).trim() : ORBIT;

    var viewer = null;
    var loadedOnce = false;
    var token = 0;
    var timer = null;

    function selection() {
      var out = [];
      if (!cfgRoot) return out;
      cfgRoot.querySelectorAll('.hh-cfg__radio:checked').forEach(function (r) {
        var d = cfg.options[r.dataset.hhCfgOption];
        if (d) out.push(d);
      });
      return out;
    }

    function applyMaterials() {
      if (!viewer || !viewer.model) return;
      var byName = {};
      viewer.model.materials.forEach(function (m) { byName[m.name] = m; });
      var missing = [];

      selection().forEach(function (d) {
        var mat = byName[d.target];
        if (!mat) return;
        if (!has(d.color) && !has(d.roughness)) { missing.push(d.name); return; }
        if (has(d.color)) mat.pbrMetallicRoughness.setBaseColorFactor(hexToLinear(d.color));
        if (has(d.roughness)) mat.pbrMetallicRoughness.setRoughnessFactor(parseFloat(d.roughness));
      });

      if (missing.length) {
        note.textContent = 'The 3D view does not yet represent ' + missing.join(', ') + '.';
        note.hidden = false;
      } else {
        note.hidden = true;
        note.textContent = '';
      }
    }

    function setState(s) {
      stage.dataset.state = s;
      loading.hidden = s !== 'loading';
      errBox.hidden = s !== 'error';
      if (controls) controls.hidden = s !== 'ready';
    }

    function setView(is3d) {
      if (visual) visual.dataset.view = is3d ? '3d' : 'photos';
      openBtns.forEach(function (b) {
        b.setAttribute('aria-selected', is3d ? 'true' : 'false');
        b.setAttribute('aria-expanded', is3d ? 'true' : 'false');
      });
      closeBtns.forEach(function (b) {
        b.setAttribute('aria-selected', is3d ? 'false' : 'true');
      });
    }

    function clearTimer() { if (timer) { clearTimeout(timer); timer = null; } }
    function dropHint() { if (hint) hint.hidden = true; }

    function spinning() { return !!spinBtn && spinBtn.getAttribute('aria-pressed') === 'true'; }

    function setSpin(on) {
      if (!spinBtn) return;
      spinBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (!viewer) return;
      if (on) {
        viewer.setAttribute('auto-rotate-delay', '0');
        viewer.setAttribute('rotation-per-second', SPIN_SPEED);
        viewer.setAttribute('auto-rotate', '');
      } else {
        viewer.removeAttribute('auto-rotate');
      }
    }

    function open() {
      setView(true);
      stage.hidden = false;

      // A viewer already exists: show it. Creating a second one here is the
      // bug that switching views twice would otherwise introduce.
      if (viewer) {
        if (loadedOnce) { setState('ready'); applyMaterials(); if (spinning()) setSpin(true); }
        return;
      }

      var mine = ++token;
      setState('loading');

      loadLibrary().then(function () {
        if (mine !== token) return;

        var mv = document.createElement('model-viewer');
        mv.className = 'hh-v3d__viewer';
        // Must be set before src: without it model-viewer defers to its own
        // preload heuristic and never starts.
        mv.setAttribute('loading', 'eager');
        mv.setAttribute('reveal', 'auto');
        mv.setAttribute('src', cfg.model);
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('touch-action', 'pan-y');
        mv.setAttribute('camera-orbit', startOrbit);
        mv.setAttribute('camera-target', 'auto auto auto');
        mv.setAttribute('field-of-view', FOV + 'deg');
        mv.setAttribute('min-field-of-view', FOV_MIN + 'deg');
        mv.setAttribute('max-field-of-view', FOV_MAX + 'deg');
        mv.setAttribute('min-camera-orbit', 'auto auto 1m');
        mv.setAttribute('max-camera-orbit', 'auto auto 4m');
        mv.setAttribute('environment-image', 'neutral');
        mv.setAttribute('exposure', '1.05');
        mv.setAttribute('shadow-intensity', '1.1');
        mv.setAttribute('shadow-softness', '0.9');
        mv.setAttribute('interaction-prompt', 'none');
        mv.setAttribute('alt', root.dataset.alt || 'Three dimensional view of this piece');

        mv.addEventListener('load', function () {
          if (mine !== token) return;
          clearTimer();
          viewer = mv;
          loadedOnce = true;
          setState('ready');
          applyMaterials();
          if (spinning()) setSpin(true);
        }, { once: true });

        mv.addEventListener('error', function () {
          if (mine !== token || loadedOnce) return;
          clearTimer();
          setState('error');
        });

        mv.addEventListener('webglcontextlost', function (e) { e.preventDefault(); });
        mv.addEventListener('pointerdown', dropHint, { once: true });
        mv.addEventListener('wheel', dropHint, { once: true, passive: true });

        host.appendChild(mv);
        viewer = mv;

        clearTimer();
        timer = setTimeout(function () {
          if (mine !== token || loadedOnce) return;
          setState('error');
        }, LOAD_TIMEOUT);
      }).catch(function () {
        if (mine !== token) return;
        clearTimer();
        setState('error');
      });
    }

    function close() {
      // Stop the turntable first: a hidden canvas should not keep rendering.
      if (viewer) viewer.removeAttribute('auto-rotate');
      setView(false);
      stage.hidden = true;
    }

    // --- controls ---------------------------------------------------------
    function orbitBy(deg) {
      if (!viewer || !loadedOnce) return;
      var o = viewer.getCameraOrbit();
      viewer.cameraOrbit =
        (o.theta * 180 / Math.PI + deg) + 'deg ' +
        (o.phi * 180 / Math.PI) + 'deg ' + o.radius + 'm';
    }
    function zoomBy(delta) {
      if (!viewer || !loadedOnce) return;
      var fov = viewer.getFieldOfView();
      viewer.fieldOfView = Math.min(FOV_MAX, Math.max(FOV_MIN, fov + delta)) + 'deg';
    }
    function resetView() {
      if (!viewer || !loadedOnce) return;
      setSpin(false);
      viewer.cameraOrbit = startOrbit;
      viewer.fieldOfView = FOV + 'deg';
    }

    if (controls) {
      controls.addEventListener('click', function (e) {
        var b = e.target.closest('[data-hh-v3d-act]');
        if (!b) return;
        dropHint();
        var a = b.dataset.hhV3dAct;
        if (a === 'spin') setSpin(!spinning());
        else if (a === 'left') orbitBy(-30);
        else if (a === 'right') orbitBy(30);
        else if (a === 'in') zoomBy(-5);
        else if (a === 'out') zoomBy(5);
        else if (a === 'reset') resetView();
      });
    }

    openBtns.forEach(function (b) { b.addEventListener('click', open); });
    closeBtns.forEach(function (b) { b.addEventListener('click', close); });

    if (retry) {
      retry.addEventListener('click', function () {
        host.innerHTML = '';
        viewer = null;
        loadedOnce = false;
        token++;
        open();
      });
    }

    if (cfgRoot) {
      cfgRoot.addEventListener('change', function (e) {
        if (e.target.matches('.hh-cfg__radio')) applyMaterials();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !stage.hidden) close();
    });

    setView(false);
  }

  function boot() { document.querySelectorAll('[data-hh-v3d]').forEach(init); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
  document.addEventListener('shopify:section:load', boot);
})();
